import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AddressPayload } from '@/lib/api/email-flows';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib';
import { QUERY_KEYS } from '@/lib/constants';
import { executeOnboardingFlow } from '../logic/onboarding.logic';
import { NOTIFICATION_MESSAGES } from '../constants';

export function useEmailOnboarding() {
    const [error, setError] = useState<Error | null>(null);
    const { user, refetchUser } = useUser();
    const queryClient = useQueryClient();

    const { mutateAsync: submitAddress, isPending: isSubmitting } = useMutation({
        mutationFn: async (address: AddressPayload) => {
            setError(null);

            if (!user?.user_id) {
                throw new Error('User ID not found');
            }

            try {
                await executeOnboardingFlow(address, Number(user.user_id));
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to complete onboarding';
                setError(err instanceof Error ? err : new Error(errorMessage));
                stanNotify({
                    type: 'error',
                    title: NOTIFICATION_MESSAGES.ONBOARDING.ERROR.title,
                    text: errorMessage,
                    duration: NOTIFICATION_MESSAGES.ONBOARDING.ERROR.duration,
                });
                throw err;
            }
        },
    });

    const { mutateAsync: completeOnboarding, isPending: isCompletingOnboarding } = useMutation({
        mutationFn: async () => {
            try {
                // Invalidate email flows queries to refetch with fresh data
                await Promise.all([
                    refetchUser(),
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] }),
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] }),
                ]);

                stanNotify({
                    type: 'success',
                    title: NOTIFICATION_MESSAGES.ONBOARDING.SUCCESS.title,
                    duration: NOTIFICATION_MESSAGES.ONBOARDING.SUCCESS.duration,
                });
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to complete onboarding';
                stanNotify({
                    type: 'error',
                    title: NOTIFICATION_MESSAGES.ONBOARDING.FINALIZE_ERROR.title,
                    text: errorMessage,
                    duration: NOTIFICATION_MESSAGES.ONBOARDING.FINALIZE_ERROR.duration,
                });
                throw err;
            }
        },
    });

    return {
        submitAddress,
        isSubmitting,
        completeOnboarding,
        isCompletingOnboarding,
        error,
    };
}
